import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Palette } from 'lucide-react';

// Derive the material type (e.g. 'resin' / 'filament') from a material key
function deriveType(materials, materialKey) {
  if (!materials || !materialKey) return Object.keys(materials || {})[0] || 'filament';
  for (const type of Object.keys(materials)) {
    if (materials[type]?.[materialKey]) return type;
  }
  return Object.keys(materials)[0] || 'filament';
}

export default function MaterialSelector({ materials, selectedMaterial, onMaterialChange, onColorChange, selectedColor }) {
  // Hooks must always be called before any conditional return (Rules of Hooks)
  // Use a lazy initializer so the correct type is set from the very first render
  // (e.g. when selectedMaterial is restored from localStorage)
  const [selectedType, setSelectedType] = useState(() => deriveType(materials, selectedMaterial));

  // Keep selectedType in sync when selectedMaterial or materials change externally
  useEffect(() => {
    if (!selectedMaterial || !materials) return;
    setSelectedType(deriveType(materials, selectedMaterial));
  }, [selectedMaterial, materials]);

  if (!materials) return null;

  const availableMaterials = materials[selectedType] || {};
  const availableColors = selectedMaterial ? materials[selectedType]?.[selectedMaterial]?.colors || [] : [];

  const handleTypeChange = (type) => {
    setSelectedType(type);
    onMaterialChange(null);
    onColorChange(null);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Palette className="w-5 h-5" />
          Material & Farbe auswählen
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Druckverfahren</label>
          <Select value={selectedType} onValueChange={handleTypeChange}>
            <SelectTrigger>
              <SelectValue placeholder="Druckverfahren wählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="resin">Resin (SLA)</SelectItem>
              <SelectItem value="filament">Filament (FDM)</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Material</label>
          <Select value={selectedMaterial || ''} onValueChange={onMaterialChange}>
            <SelectTrigger>
              <SelectValue placeholder="Material wählen" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(availableMaterials).map(([key, material]) => (
                <SelectItem key={key} value={key}>
                  {material.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {selectedMaterial && (
          <div>
            <label className="text-sm font-medium mb-2 block">Farbe</label>
            <Select value={selectedColor || ''} onValueChange={onColorChange}>
              <SelectTrigger>
                <SelectValue placeholder="Farbe wählen" />
              </SelectTrigger>
              <SelectContent>
                {availableColors.map((color) => (
                  <SelectItem key={color} value={color}>
                    {color}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-2">
              <Badge variant="secondary">
                {materials[selectedType]?.[selectedMaterial]?.name}
              </Badge>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
